'use client';

import type { ChangeEvent, InputEvent, InputHTMLAttributes } from 'react';
import { forwardRef, useId, useState } from 'react';
import { cx } from '../utils/cx';
import { Label } from './label';

import type { IconName, ResolvedIcon } from '../../packages/icons/icon-registry';
import { IconRegistry } from '../../packages/icons/icon-registry';

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helperText?: string;
  hideLabel?: boolean;
  size?: InputSize;

  icon?: IconName | string | null;
  iconPosition?: 'left' | 'right';
  iconSize?: number;

  isValid?: boolean;
  isValidating?: boolean;
}

function coerceBoolean(value: unknown, defaultValue: boolean): boolean {
  if (typeof value === 'boolean') return value;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false' || normalized === '') return false;
    if (normalized === '1' || normalized === 'yes' || normalized === 'on') return true;
    if (normalized === '0' || normalized === 'no' || normalized === 'off') return false;
  }

  if (typeof value === 'number') return value !== 0;

  return defaultValue;
}

function sizeClass(size: InputSize): string {
  switch (size) {
    case 'sm':
      return 'h-10 text-sm';
    case 'lg':
      return 'h-12 text-base';
    case 'md':
    default:
      return 'h-11 text-sm';
  }
}

const warnedMissingIcons = new Set<string>();

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    helperText,
    hideLabel = false,
    size = 'md',
    icon,
    iconPosition = 'left',
    iconSize = 20,
    className,
    id,
    name,
    required,
    isValid = true,
    isValidating = false,
    disabled,
    value,
    defaultValue,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const controlId = id ?? (typeof name === 'string' && name.length > 0 ? name : autoId);

  // Always supply a defined value so an asynchronous value binding cannot
  // switch the underlying input from uncontrolled to controlled. When no
  // binding is supplied, state retains typed text across re-renders.
  const [unboundValue, setUnboundValue] = useState<string | number | readonly string[]>(
    () => value ?? defaultValue ?? '',
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUnboundValue(event.currentTarget.value);
    rest.onChange?.(event);
  };

  const handleInput = (event: InputEvent<HTMLInputElement>) => {
    setUnboundValue(event.currentTarget.value);
    rest.onInput?.(event);
  };

  const disabledBool = coerceBoolean(disabled, false);
  const requiredBool = coerceBoolean(required, false);
  const isValidBool = coerceBoolean(isValid, true);
  const isValidatingBool = coerceBoolean(isValidating, false);

  const hasIconRequested = Boolean(icon);
  const IconComponent: ResolvedIcon | null = icon ? IconRegistry.get(String(icon)) : null;

  if (
    process.env.NODE_ENV !== 'production' &&
    typeof window !== 'undefined' &&
    hasIconRequested &&
    icon &&
    !IconComponent
  ) {
    const key = String(icon);
    if (!warnedMissingIcons.has(key)) {
      warnedMissingIcons.add(key);
      // eslint-disable-next-line no-console
      console.warn(`[Input] Icon "${key}" was requested but not found in IconRegistry.`);
    }
  }

  const shouldReserveIconLeft = hasIconRequested && iconPosition === 'left';
  const shouldReserveIconRight = hasIconRequested && iconPosition === 'right';

  const inputPaddingForIcon = cx(
    shouldReserveIconLeft && 'pl-11 pr-4',
    shouldReserveIconRight && 'pl-4 pr-11',
    !hasIconRequested && 'px-4',
  );

  const controlledValue = value ?? unboundValue;

  const inputEl = (
    <div className={cx('relative w-full', hasIconRequested && 'group')}>
      {hasIconRequested ? (
        <span
          aria-hidden="true"
          className={cx(
            'pointer-events-none absolute top-1/2 -translate-y-1/2 text-tertiary transition-colors',
            iconPosition === 'left' ? 'left-4' : 'right-4',
            'group-focus-within:text-secondary',
          )}
        >
          {IconComponent ? (
            <IconComponent size={iconSize} strokeWidth={2} className="shrink-0" />
          ) : (
            <span style={{ width: iconSize, height: iconSize }} className="block shrink-0" />
          )}
        </span>
      ) : null}

      <input
        {...rest}
        ref={ref}
        id={controlId}
        name={name}
        required={requiredBool}
        disabled={disabledBool}
        aria-disabled={disabledBool || undefined}
        aria-invalid={!isValidBool || undefined}
        aria-busy={isValidatingBool || undefined}
        data-validating={isValidatingBool ? 'true' : undefined}
        value={controlledValue}
        onChange={handleChange}
        onInput={handleInput}
        className={cx(
          'block w-full appearance-none rounded-xl border bg-primary text-secondary shadow-xs',
          'border-secondary_alt/80 py-1',
          'transition-all',
          'placeholder:text-tertiary',
          'focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10',
          'disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-secondary',
          !isValidBool && 'border-danger focus:border-danger focus:ring-danger/10',
          'leading-none',
          sizeClass(size),
          inputPaddingForIcon,
          className,
        )}
      />
    </div>
  );

  if (!label) return inputEl;

  return (
    <div className="w-full">
      {hideLabel ? (
        <label htmlFor={controlId} className="sr-only">
          {label}
        </label>
      ) : (
        <Label htmlFor={controlId} required={requiredBool} helperText={helperText}>
          {label}
        </Label>
      )}
      <div className="mt-1.5">{inputEl}</div>
    </div>
  );
});

Input.displayName = 'Input';